// v1.6.67: system health snapshot + status derivation
// Shared between the SystemHealth module (full page) and the header status dot.

import { supabase } from './supabase';

// Graph client secret expiry — set this whenever secret is rotated.
// Reading it from an env var lets you bump without a code deploy.
const GRAPH_SECRET_EXPIRES_AT = process.env.REACT_APP_GRAPH_SECRET_EXPIRES_AT || '';

export async function fetchSystemHealth() {
  const { data, error } = await supabase.rpc('system_health_snapshot');
  if (error) throw error;
  const snap = data || {};
  return deriveStatus(snap);
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
}

// Returns { overall: 'green'|'amber'|'red', checks: [{key,label,status,detail,value}], snap }
export function deriveStatus(snap) {
  const checks = [];
  const pipe = snap?.pipeline || {};
  const s24 = snap?.sends_24h || {};

  // v1.7.41.228 -- real, Stinger direct: "no reason to have it if it's
  // not wired and actually monitoring." The old placeholder here was
  // hardcoded to always show amber, forever, regardless of real
  // health -- removed, then rebuilt for real against
  // cron_dispatch_state (#1698's real consolidated dispatcher), which
  // system_health_snapshot() now returns as cron_jobs. Judged against
  // each job's OWN real schedule cadence, not one blanket threshold --
  // a weekly job "overdue" by 600 minutes is normal; a */5 job overdue
  // by 20 minutes is not. Real, found and fixed while wiring this:
  // the dispatcher's success path never cleared last_error, so every
  // job showed the exact "Cannot find module" text from a real bug
  // already fixed in v1.7.41.133 -- stale forever, not current. Fixed
  // separately in api/cron-dispatcher.js; this check reads accurate
  // data now, not the leftover artifact.
  const EXPECTED_INTERVAL_MIN = {
    'triplog-cron': 240, 'sequence-cron': 5, 'contact-partners-cron': 5,
    'scheduled-send-cron': 5, 'route-processor-events': 5,
    'genserv-nourishment-cron': 1440, 'workflow-cron': 60,
    'dedupe-scan': 10080, 'duplicate-sweep': 1440, 'bounce-detection-cron': 15,
    'goldmine-inbound-cron': 15, 'bounce-forward-cron': 15,
    'between-roles-recheck-cron': 1440, 'company-quarterly-recheck-cron': 1440,
    'birthday-reminder-cron': 1440, 'daily-recap-cron': 1440,
    'ledger-audit-cron': 720, 'eod-report-cron': 1440,
    'ready-room-cron': 15, 'area52-bridge-cron': 2, 'wake-dispatcher': 10,
    'iceman-generate-cron': 60, 'renew-graph-subscription': 1440,
  };
  const cronJobs = (snap?.cron_jobs || []).filter((j) => j.enabled !== false);
  const now = Date.now();
  const cronProblems = cronJobs.filter((j) => {
    const expected = EXPECTED_INTERVAL_MIN[j.job_name] || 60;
    const minsSince = j.last_fired_at ? (now - new Date(j.last_fired_at).getTime()) / 60000 : Infinity;
    return j.last_error || j.last_status_code >= 400 || minsSince > expected * 4;
  });
  const cronWarnings = cronJobs.filter((j) => {
    if (cronProblems.includes(j)) return false;
    const expected = EXPECTED_INTERVAL_MIN[j.job_name] || 60;
    const minsSince = j.last_fired_at ? (now - new Date(j.last_fired_at).getTime()) / 60000 : Infinity;
    return minsSince > expected * 2;
  });
  if (cronJobs.length === 0) {
    checks.push({ key: 'cron', label: 'Cron heartbeat', status: 'amber', detail: 'No real job data yet — dispatcher may not have fired since deploy', value: '—' });
  } else if (cronProblems.length > 0) {
    checks.push({ key: 'cron', label: 'Cron heartbeat', status: 'red', detail: cronProblems.map((j) => j.job_name).join(' · '), value: `${cronProblems.length} failing/overdue` });
  } else if (cronWarnings.length > 0) {
    checks.push({ key: 'cron', label: 'Cron heartbeat', status: 'amber', detail: cronWarnings.map((j) => j.job_name).join(' · '), value: `${cronWarnings.length} running late` });
  } else {
    checks.push({ key: 'cron', label: 'Cron heartbeat', status: 'green', detail: `${cronJobs.length} jobs, all on schedule`, value: `${cronJobs.length}/${cronJobs.length}` });
  }

  // Send delivery — the truth check that catches silent Graph auth failures
  const active = pipe.active_enrollments || 0;
  const nextDue = pipe.next_due_at ? new Date(pipe.next_due_at).getTime() : null;
  if (active > 0 && nextDue && nextDue < Date.now() - 15 * 60 * 1000 && (s24.total || 0) === 0) {
    checks.push({ key: 'delivery', label: 'Send engine delivering', status: 'red', detail: `${active} active enrollments, next was due ${Math.round((Date.now() - nextDue) / 60000)}m ago, zero sends in 24h`, value: '0 / 24h' });
  } else if (active > 0 && nextDue && nextDue < Date.now() && (s24.total || 0) > 0) {
    // v1.7.39.208 -- real fix, Shane's catch 2026-08-03: this used to
    // fire purely on "24h+ since last success", regardless of whether
    // anything was actually due -- with real enrollments legitimately
    // spaced days apart, that's completely normal, not a real amber
    // signal. Real fix: only flag amber when something is genuinely
    // overdue right now (the red case above already covers the total-
    // outage version of this same real signal, zero sends AND overdue).
    checks.push({ key: 'delivery', label: 'Send engine delivering', status: 'amber', detail: `${active} active enrollments, next was due ${Math.round((Date.now() - nextDue) / 60000)}m ago -- some sends happening, check for lag`, value: s24.last_success_at || '—' });
  } else if (active === 0) {
    checks.push({ key: 'delivery', label: 'Send engine delivering', status: 'green', detail: 'No active enrollments to send', value: '—' });
  } else {
    checks.push({ key: 'delivery', label: 'Send engine delivering', status: 'green', detail: `${s24.total} sends in 24h, ${s24.failed} failed`, value: `${s24.total} / 24h` });
  }

  // Failure rate
  if ((s24.total || 0) > 0) {
    const rate = (s24.failed || 0) / s24.total;
    if (rate > 0.25) {
      checks.push({ key: 'errors', label: 'Send failure rate under 25%', status: 'red', detail: `${Math.round(rate * 100)}% failed in 24h`, value: `${s24.failed} / ${s24.total}` });
    } else if (rate > 0.05) {
      checks.push({ key: 'errors', label: 'Send failure rate under 5%', status: 'amber', detail: `${Math.round(rate * 100)}% failed in 24h`, value: `${s24.failed} / ${s24.total}` });
    } else {
      checks.push({ key: 'errors', label: 'Send failure rate under 5%', status: 'green', detail: `${Math.round(rate * 100)}% failed in 24h`, value: `${s24.failed} / ${s24.total}` });
    }
  }

  // Graph client secret expiry countdown
  if (GRAPH_SECRET_EXPIRES_AT) {
    const days = daysUntil(GRAPH_SECRET_EXPIRES_AT);
    if (days == null || days < 0) {
      checks.push({ key: 'graph_secret', label: 'Graph client secret valid', status: 'red', detail: 'Secret expired', value: GRAPH_SECRET_EXPIRES_AT });
    } else if (days < 14) {
      checks.push({ key: 'graph_secret', label: 'Graph secret over 14d away', status: 'red', detail: `Expires in ${days}d — rotate now`, value: GRAPH_SECRET_EXPIRES_AT });
    } else if (days < 45) {
      checks.push({ key: 'graph_secret', label: 'Graph secret over 45d away', status: 'amber', detail: `Expires in ${days}d`, value: GRAPH_SECRET_EXPIRES_AT });
    } else {
      checks.push({ key: 'graph_secret', label: 'Graph secret over 45d away', status: 'green', detail: `Expires in ${days}d`, value: GRAPH_SECRET_EXPIRES_AT });
    }
  } else {
    checks.push({ key: 'graph_secret', label: 'Graph secret expiry tracked', status: 'amber', detail: 'REACT_APP_GRAPH_SECRET_EXPIRES_AT not set — set in Vercel env after rotating', value: '—' });
  }

  // Pipeline backpressure
  if (active > 500) {
    checks.push({ key: 'pipeline', label: 'Active enrollments under 500', status: 'amber', detail: `${active} active enrollments — heavy pipeline`, value: active });
  } else {
    checks.push({ key: 'pipeline', label: 'Active enrollments', status: 'green', detail: `${active} active`, value: active });
  }

  // v1.6.70 — Vercel deployment freshness. Baked at build time via REACT_APP_BUILD_TIME.
  const buildTime = process.env.REACT_APP_BUILD_TIME || '';
  const buildAgeDays = buildTime ? Math.floor((Date.now() - new Date(buildTime).getTime()) / 86400000) : null;
  if (!buildTime) {
    checks.push({ key: 'vercel_fresh', label: 'Vercel deploy freshness', status: 'amber', detail: 'Build timestamp not set', value: '—' });
  } else if (buildAgeDays > 30) {
    checks.push({ key: 'vercel_fresh', label: 'Vercel deploy under 30 days', status: 'red', detail: `Last build ${buildAgeDays}d ago`, value: buildTime });
  } else if (buildAgeDays > 14) {
    checks.push({ key: 'vercel_fresh', label: 'Vercel deploy under 14 days', status: 'amber', detail: `Last build ${buildAgeDays}d ago`, value: buildTime });
  } else {
    checks.push({ key: 'vercel_fresh', label: 'Vercel deploy fresh', status: 'green', detail: `Last build ${buildAgeDays}d ago`, value: buildTime });
  }

  // v1.6.70 — DB row sanity checks. Flag red on suspicious drops (>10% loss vs yesterday) or empty tables.
  const rows = snap?.rows || {};
  const contactsNow = rows.contacts?.now ?? null;
  const companiesNow = rows.companies?.now ?? null;
  if (contactsNow == null || companiesNow == null) {
    checks.push({ key: 'row_counts', label: 'DB row sanity', status: 'amber', detail: 'Row snapshot unavailable', value: '—' });
  } else if (contactsNow < 100 || companiesNow < 50) {
    checks.push({ key: 'row_counts', label: 'DB row sanity', status: 'red', detail: `Contacts=${contactsNow}, Companies=${companiesNow} — catastrophic drop`, value: `${contactsNow}c / ${companiesNow}co` });
  } else {
    checks.push({ key: 'row_counts', label: 'DB row sanity', status: 'green', detail: `${contactsNow} contacts · ${companiesNow} companies · ${rows.active_enrollments ?? 0} active enroll · ${rows.sequence_sends_24h ?? 0} sends 24h`, value: `${contactsNow}c / ${companiesNow}co` });
  }

  // Roll-up: red beats amber beats green
  let overall = 'green';
  for (const c of checks) {
    if (c.status === 'red') { overall = 'red'; break; }
    if (c.status === 'amber') overall = 'amber';
  }

  return { overall, checks, snap };
}

export const STATUS_COLOR = {
  green: 'var(--gov-success)',
  amber: '#d97706',
  red: 'var(--gov-brand-red)',
};
